import React, { useState } from 'react'

import { Form, Formik } from 'formik'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import { setEscalationApprovalDrawerOpen } from '@/client/generalSlice'
import { useAppDispatch, useAppSelector } from '@/client/hooks'
import { SelectField } from '@/components/FormikInputs'
import TextEditorField from '@/components/FormikInputs/v2/TextEditorField'
import Stepper from '@/components/Stepper'
import { escalationFlagsTypes, escalationReviewOptions } from '@/constants/escalations'
import { useERAdjusmentOptions } from '@/features/DealSummary/AssetManagementOverridesSection'
import { solarExceptionPathOptions } from '@/features/application/applicationSlice'
import { useCurrentApplication } from '@/graphql/services/application'
import { useUpdateEscalationMutation } from '@/services/escalations'
import { toastDefaultOptions } from '@/utils/toast'

type EscalationFormValues = {
  uuid?: string
  approved?: boolean
  exchange_rate_adjustment?: string
  path_solar_panel?:
    | 'financed-not-paying-off'
    | 'financed-paying-off-as-condition'
    | 'owned-free-and-clear'
    | ''
    | null
  tagged_users?: string[] | number[]
  request_comment?: string | null
  response_comment?: string | null
  comment?: string | null
}

const skipStep2Escalations = [
  'missing-dti',
  'missing-debt-service-coverage-ratio',
  'debt-service-coverage-ratio',
]

const EscalationApproval = () => {
  const dispatch = useAppDispatch()
  const { data: application } = useCurrentApplication()

  const [message, setMessage] = useState('')
  const [isError, setIsError] = useState(false)

  const adjustmentOptions = useERAdjusmentOptions()

  const escalation = useAppSelector((state) => state.general.escalationApprovalDrawerEscalation)

  const isEscalationException =
    escalation.tab === 'exceptions' && !escalationFlagsTypes.includes(escalation.type)

  const [updateEscalation] = useUpdateEscalationMutation()

  const resetDrawer = () => {
    setMessage('')
    setIsError(false)
  }

  const exchangeRateAdjustment = () => {
    if (escalation?.type === 'third-lien') {
      return application?.pricingVersion >= 18 ? '0.00' : '0.05'
    }
    if (escalation?.type === 'investment-size') {
      return '0.05'
    }
    return '0.00'
  }

  return (
    <Formik
      initialValues={{
        exchange_rate_adjustment: exchangeRateAdjustment(),
        path_solar_panel: '',
        comment: '',
      }}
      validationSchema={Yup.object().shape({
        path_solar_panel: Yup.string()
          .typeError('Solar Exception Path is required')
          .when([], {
            is: () => escalation?.type === 'roof-solar-panel',
            then: Yup.string()
              .typeError('Solar Exception Path is required')
              .required('Solar Exception Path is required'),
            otherwise: Yup.string().nullable(),
          }),
      })}
      enableReinitialize
      onSubmit={async (values: EscalationFormValues, actions) => {
        try {
          const payload: EscalationFormValues = {
            uuid: escalation.uuid,
            approved: true,
            exchange_rate_adjustment: values?.exchange_rate_adjustment,
            path_solar_panel: values?.path_solar_panel ? values?.path_solar_panel : null,
            tagged_users: escalation.tagged_users || [],
            request_comment: null,
            response_comment: null,
          }

          if (isEscalationException) {
            payload.request_comment = values.comment ? values.comment : null
          } else {
            payload.response_comment = values.comment ? values.comment : null
          }

          const data: any = await updateEscalation(payload)

          actions.setSubmitting(false)

          if (data.error) {
            setIsError(true)
            return setMessage('There was an error on the request, please try again')
          } else {
            if (data.data.is_success == false) {
              setIsError(true)
              return setMessage('There was an error on the request, please try again')
            }
          }
          toast.success('The escalation review was created successfully', {
            ...toastDefaultOptions,
          })
          resetDrawer()
          dispatch(setEscalationApprovalDrawerOpen(false))
        } catch (error) {
          setIsError(true)
          setMessage('There was an error on the request, please try again')
        } finally {
          actions.setSubmitting(false)
        }
      }}
      validateOnMount
    >
      {({
        isSubmitting,
        isValid,
        setFieldValue,
        submitForm,
        setTouched,
        touched,
        validateForm,
      }) => {
        return (
          <Form>
            <Stepper
              initialStep={skipStep2Escalations.includes(escalation?.type) ? 2 : 1}
              steps={[
                {
                  title: 'Escalation Type',
                  content: () => (
                    <SelectField
                      style='default'
                      name='escalationType'
                      value={escalation?.type}
                      extraClasses='w-full'
                      options={escalationReviewOptions}
                      readOnly
                      disabled
                    />
                  ),
                  buttonLabel: 'Next',
                },
                {
                  title: 'Approval Details',
                  content: () => (
                    <>
                      <SelectField
                        style='default'
                        name='exchange_rate_adjustment'
                        label='Select a pricing adjustment or leave as 0.00 if no adjustment is to be made.'
                        readOnly={isSubmitting}
                        options={adjustmentOptions}
                        hasPlaceholder={false}
                      />
                      {escalation?.type === 'roof-solar-panel' && (
                        <SelectField
                          style='default'
                          name='path_solar_panel'
                          label='Select Solar Exception Path.'
                          extraClasses='mt-4'
                          readOnly={isSubmitting}
                          options={[
                            { value: '', label: 'Select...' },
                            ...solarExceptionPathOptions,
                          ]}
                          hasPlaceholder={false}
                        />
                      )}
                    </>
                  ),
                  buttonLabel: 'Next',
                  customClick: async (currentStep, setCurrentStep) => {
                    await validateForm()
                    setTouched({
                      ...touched,
                      exchange_rate_adjustment: true,
                      path_solar_panel: true,
                    })
                    if (isValid) {
                      setCurrentStep(currentStep + 1)
                    }
                  },
                },
                {
                  title: 'Add commentary',
                  content: () => (
                    <TextEditorField
                      setFieldValue={setFieldValue}
                      name='comment'
                      defaultShowEditor
                      showSaveButton={false}
                    />
                  ),
                  buttonLabel: 'Next',
                  hideButton: true,
                },
              ]}
              finishButton={{
                label: 'Finish - Complete Review',
                onClick: () => submitForm(),
                extraDisabled: isSubmitting || !isValid,
                loadingFooter: isSubmitting,
                footerMessage: message && (
                  <div className={`${isError && 'text-red-500'}`}>{message}</div>
                ),
              }}
            />
          </Form>
        )
      }}
    </Formik>
  )
}

export default EscalationApproval
